import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/apiConfig';

const TenantManager = ({ onBack, apiBasePath = '/admin/tenants' }) => {
  // API-Endpunkt-Helper
  const getApiUrl = (path) => `${API_BASE_URL}${path}`;
  const getTenantApi = (endpoint) => getApiUrl(`${apiBasePath}${endpoint}`);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(null);

  // Formular-Daten
  const [tenantForm, setTenantForm] = useState({
    tenant_name: '',
    tenant_key: '',
    contact_email: '',
    contact_phone: ''
  });

  const [adminForm, setAdminForm] = useState({
    vorname: '',
    nachname: '',
    login: '',
    passwort: '',
    passwort_confirm: ''
  });

  const [passwordForm, setPasswordForm] = useState({
    passwort: '',
    passwort_confirm: ''
  });

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(getTenantApi(''));
      const result = await response.json();

      if (result.success) {
        setTenants(result.data);
      } else {
        setError(result.message || 'Fehler beim Laden der Mandanten');
      }
    } catch (err) {
      setError('Verbindungsfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTenantDetails = async (tenantKey) => {
    try {
      const response = await fetch(getTenantApi(`/${tenantKey}`));
      const result = await response.json();

      if (result.success) {
        setSelectedTenant(result.data);
      } else {
        setError(result.message || 'Fehler beim Laden der Details');
      }
    } catch (err) {
      setError('Verbindungsfehler: ' + err.message);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setError('');

    if (!tenantForm.tenant_name || !tenantForm.tenant_key || !tenantForm.contact_email) {
      setError('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    try {
      const response = await fetch(getTenantApi(''), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tenantForm)
      });

      const result = await response.json();

      if (result.success) {
        alert('Mandant erfolgreich erstellt!');
        setShowCreateForm(false);
        setTenantForm({ tenant_name: '', tenant_key: '', contact_email: '', contact_phone: '' });
        loadTenants();
      } else {
        setError(result.message || 'Fehler beim Erstellen');
      }
    } catch (err) {
      setError('Verbindungsfehler: ' + err.message);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setError('');

    if (!adminForm.vorname || !adminForm.nachname || !adminForm.login || !adminForm.passwort) {
      setError('Bitte alle Felder ausfüllen');
      return;
    }

    if (adminForm.passwort !== adminForm.passwort_confirm) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    if (adminForm.passwort.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    try {
      const response = await fetch(getTenantApi(`/${selectedTenant.tenant_key}/admin`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vorname: adminForm.vorname,
          nachname: adminForm.nachname,
          login: adminForm.login,
          passwort: adminForm.passwort
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('Admin-Benutzer erfolgreich erstellt!');
        setShowAdminForm(false);
        setAdminForm({ vorname: '', nachname: '', login: '', passwort: '', passwort_confirm: '' });
        loadTenantDetails(selectedTenant.tenant_key);
      } else {
        setError(result.message || 'Fehler beim Erstellen');
      }
    } catch (err) {
      setError('Verbindungsfehler: ' + err.message);
    }
  };

  const handleChangePassword = async (e, adminId) => {
    e.preventDefault();
    setError('');

    if (!passwordForm.passwort || !passwordForm.passwort_confirm) {
      setError('Bitte beide Felder ausfüllen');
      return;
    }

    if (passwordForm.passwort !== passwordForm.passwort_confirm) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    if (passwordForm.passwort.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    try {
      const response = await fetch(getTenantApi(`/${selectedTenant.tenant_key}/admin/${adminId}/password`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passwort: passwordForm.passwort })
      });

      const result = await response.json();

      if (result.success) {
        alert('Passwort erfolgreich geändert!');
        setShowPasswordForm(null);
        setPasswordForm({ passwort: '', passwort_confirm: '' });
      } else {
        setError(result.message || 'Fehler beim Ändern');
      }
    } catch (err) {
      setError('Verbindungsfehler: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="tenant-manager">
        <div className="loading">Lade Mandanten...</div>
      </div>
    );
  }

  // Details-Ansicht
  if (selectedTenant) {
    return (
      <div className="tenant-manager">
        <div className="tenant-manager-header">
          <h2>Mandanten-Details: {selectedTenant.tenant_name}</h2>
          <button onClick={() => setSelectedTenant(null)} className="btn btn-secondary">
            Zurück zur Übersicht
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="tenant-details-grid">
          {/* Allgemeine Informationen */}
          <div className="details-card">
            <h3>Allgemeine Informationen</h3>
            <table className="details-table">
              <tbody>
                <tr>
                  <td><strong>Firmenname:</strong></td>
                  <td>{selectedTenant.tenant_name}</td>
                </tr>
                <tr>
                  <td><strong>Login-Schlüssel:</strong></td>
                  <td><code>{selectedTenant.tenant_key}</code></td>
                </tr>
                <tr>
                  <td><strong>Datenbank:</strong></td>
                  <td>{selectedTenant.db_file}</td>
                </tr>
                <tr>
                  <td><strong>Email:</strong></td>
                  <td>{selectedTenant.contact_email}</td>
                </tr>
                <tr>
                  <td><strong>Telefon:</strong></td>
                  <td>{selectedTenant.contact_phone || 'N/A'}</td>
                </tr>
                <tr>
                  <td><strong>Status:</strong></td>
                  <td>
                    <span className={`status-badge ${selectedTenant.is_active ? 'active' : 'inactive'}`}>
                      {selectedTenant.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td><strong>Erstellt:</strong></td>
                  <td>{new Date(selectedTenant.created_at).toLocaleString('de-DE')}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Statistiken */}
          <div className="details-card">
            <h3>Statistiken</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-value">{selectedTenant.stats.mitarbeiter}</div>
                <div className="stat-label">Mitarbeiter</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{selectedTenant.stats.auftraege}</div>
                <div className="stat-label">Aufträge</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{selectedTenant.stats.stundenarten}</div>
                <div className="stat-label">Stundenarten</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{selectedTenant.stats.stunden}</div>
                <div className="stat-label">Zeiteinträge</div>
              </div>
            </div>
          </div>

          {/* Admin-Benutzer */}
          <div className="details-card full-width">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>Admin-Benutzer</h3>
              <button onClick={() => setShowAdminForm(true)} className="btn btn-primary">
                + Admin erstellen
              </button>
            </div>

            {showAdminForm && (
              <form onSubmit={handleCreateAdmin} className="admin-form">
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Vorname *"
                    value={adminForm.vorname}
                    onChange={(e) => setAdminForm({ ...adminForm, vorname: e.target.value })}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Nachname *"
                    value={adminForm.nachname}
                    onChange={(e) => setAdminForm({ ...adminForm, nachname: e.target.value })}
                    required
                  />
                </div>
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Login *"
                    value={adminForm.login}
                    onChange={(e) => setAdminForm({ ...adminForm, login: e.target.value })}
                    required
                  />
                </div>
                <div className="form-row">
                  <input
                    type="password"
                    placeholder="Passwort (min. 6 Zeichen) *"
                    value={adminForm.passwort}
                    onChange={(e) => setAdminForm({ ...adminForm, passwort: e.target.value })}
                    required
                  />
                  <input
                    type="password"
                    placeholder="Passwort bestätigen *"
                    value={adminForm.passwort_confirm}
                    onChange={(e) => setAdminForm({ ...adminForm, passwort_confirm: e.target.value })}
                    required
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">Erstellen</button>
                  <button type="button" onClick={() => setShowAdminForm(false)} className="btn btn-secondary">
                    Abbrechen
                  </button>
                </div>
              </form>
            )}

            {selectedTenant.stats.admins.length > 0 ? (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Login</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedTenant.stats.admins.map((admin) => (
                    <tr key={admin.mitid}>
                      <td>{admin.mitvorname} {admin.mitnachname}</td>
                      <td><code>{admin.mitlogin}</code></td>
                      <td>
                        {showPasswordForm === admin.mitid ? (
                          <form onSubmit={(e) => handleChangePassword(e, admin.mitid)} className="password-form-inline">
                            <input
                              type="password"
                              placeholder="Neues Passwort"
                              value={passwordForm.passwort}
                              onChange={(e) => setPasswordForm({ ...passwordForm, passwort: e.target.value })}
                              required
                            />
                            <input
                              type="password"
                              placeholder="Bestätigen"
                              value={passwordForm.passwort_confirm}
                              onChange={(e) => setPasswordForm({ ...passwordForm, passwort_confirm: e.target.value })}
                              required
                            />
                            <button type="submit" className="btn btn-sm btn-primary">Speichern</button>
                            <button type="button" onClick={() => setShowPasswordForm(null)} className="btn btn-sm btn-secondary">
                              Abbrechen
                            </button>
                          </form>
                        ) : (
                          <button
                            onClick={() => setShowPasswordForm(admin.mitid)}
                            className="btn btn-sm btn-warning"
                          >
                            Passwort ändern
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Keine Admin-Benutzer vorhanden</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Übersicht
  return (
    <div className="tenant-manager">
      <div className="tenant-manager-header">
        <h2>Mandanten-Verwaltung</h2>
        <div>
          <button onClick={() => setShowCreateForm(!showCreateForm)} className="btn btn-primary">
            {showCreateForm ? 'Abbrechen' : '+ Neuer Mandant'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showCreateForm && (
        <form onSubmit={handleCreateTenant} className="tenant-create-form">
          <h3>Neuer Mandant erstellen</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Firmenname *</label>
              <input
                type="text"
                value={tenantForm.tenant_name}
                onChange={(e) => setTenantForm({ ...tenantForm, tenant_name: e.target.value })}
                placeholder="z.B. Roffler Ingenieure GmbH"
                required
              />
            </div>
            <div className="form-group">
              <label>Login-Schlüssel * <small>(für App-Anmeldung)</small></label>
              <input
                type="text"
                value={tenantForm.tenant_key}
                onChange={(e) => setTenantForm({ ...tenantForm, tenant_key: e.target.value.toLowerCase() })}
                placeholder="z.B. roffler"
                pattern="[a-z0-9\-]+"
                title="Nur Kleinbuchstaben, Zahlen und Bindestriche"
                required
              />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={tenantForm.contact_email}
                onChange={(e) => setTenantForm({ ...tenantForm, contact_email: e.target.value })}
                placeholder="info@firma.ch"
                required
              />
            </div>
            <div className="form-group">
              <label>Telefon</label>
              <input
                type="tel"
                value={tenantForm.contact_phone}
                onChange={(e) => setTenantForm({ ...tenantForm, contact_phone: e.target.value })}
                placeholder="+41 81 123 45 67"
              />
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">Mandant erstellen</button>
            <button type="button" onClick={() => setShowCreateForm(false)} className="btn btn-secondary">
              Abbrechen
            </button>
          </div>
        </form>
      )}

      <div className="tenants-grid">
        {tenants.map((tenant) => (
          <div key={tenant.tenant_id} className="tenant-card">
            <div className="tenant-card-header">
              <h3>{tenant.tenant_name}</h3>
              <span className={`status-badge ${tenant.is_active ? 'active' : 'inactive'}`}>
                {tenant.is_active ? 'Aktiv' : 'Inaktiv'}
              </span>
            </div>
            <div className="tenant-card-body">
              <p><strong>Login-Schlüssel:</strong> <code>{tenant.tenant_key}</code></p>
              <p><strong>Email:</strong> {tenant.contact_email}</p>
              <div className="tenant-stats">
                <span>{tenant.stats.mitarbeiter} Mitarbeiter</span>
                <span>{tenant.stats.auftraege} Aufträge</span>
                <span>{tenant.stats.stunden} Einträge</span>
              </div>
            </div>
            <div className="tenant-card-footer">
              <button
                onClick={() => loadTenantDetails(tenant.tenant_key)}
                className="btn btn-primary btn-block"
              >
                Details anzeigen
              </button>
            </div>
          </div>
        ))}
      </div>

      {tenants.length === 0 && (
        <div className="no-tenants">
          <p>Keine Mandanten vorhanden.</p>
          <p>Erstellen Sie einen neuen Mandanten über den Button oben.</p>
        </div>
      )}
    </div>
  );
};

export default TenantManager;
